import { useState, useCallback, useEffect, useRef, memo } from 'react';
import { EditIcon, ArrowRightIcon, ArrowLeftIcon } from '../icons';
import { Input, InputProps } from 'reactstrap';
import { AvatarImage } from '../layout/AvatarImage';
import noCoverImage from '../../assets/imgs/cover-image.jpeg';
import { ImageEditorModal } from '../modals/ImageEditorModal';
import Cropper from 'react-easy-crop';
import { Button } from '../ui/button';
import { ZoomInIcon } from '../../assets/icons/ZoomInIcon';
import { ZoomOutIcon } from '../../assets/icons/ZoomOutIcon';
import { SaveIcon } from '../../assets/icons/SaveIcon';
import { ArrowsPointingOutIcon } from '../../assets/icons/ArrowsPointingOutIcon';
import { processImageForUpload } from '../../utils/imageProcessing';
import { getSimilarityConfig } from '../../api/similarity';
import { Zap } from 'lucide-react';

type InputInfoProps = InputProps & {
    label: string | number | undefined
    value: string | number | undefined
    onChange: (val: string | number | undefined) => void
}
export const InputInfo = ({
    label,
    value,
    onChange,
    onBlur,
    className,
    ...props
}: InputInfoProps) => {
    return (
        <div className="flex flex-col text-md flex-1 w-full min-w-0">
            {label ? (
                <label htmlFor="" className="mb-2 block text-sm font-bold uppercase tracking-wide text-gray-700">
                    {label}
                </label>
            ) : null}
            <div className="w-full min-w-0">
                <Input
                    className={`!h-11 !rounded-xl !border !border-gray-200 !px-3 !text-base !shadow-none focus:!border-purple-light focus:!outline-none focus:!ring-2 focus:!ring-purple-light/20 ${className || ''}`}
                    value={value ?? ''}
                    onChange={(e) => onChange && onChange(e.target.value)}
                    onBlur={(e) => onBlur && onBlur(e.target.value as any)}
                    {...props}
                />
            </div>
        </div>
    );
};

interface InputImageProps {
    src: string | undefined
    onChange: (files: FileList | null, type: string) => void
    testId: string | undefined
    canEdit?: boolean
    createdAt?: string
    isBoosted?: boolean
    alt?: string
}
export const ProfileImage = memo(({
    src,
    testId,
    onChange,
    canEdit,
    createdAt,
    isBoosted,
    alt = '',
}: InputImageProps) => {
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [newUserThreshold, setNewUserThreshold] = useState(24);

    useEffect(() => {
        const fetchSimilarityConfig = async () => {
            try {
                const config = await getSimilarityConfig();
                if (config && config.NEW_USER_DAYS_THRESHOLD) {
                    setNewUserThreshold(config.NEW_USER_DAYS_THRESHOLD);
                }
            } catch (error) {
                console.error('Error fetching similarity config:', error);
            }
        };

        fetchSimilarityConfig();
    }, []);

    const newUserLookupDate = new Date();
    newUserLookupDate.setDate(newUserLookupDate.getDate() - newUserThreshold);
    const isNewUser = createdAt && (new Date(createdAt) > newUserLookupDate);
    const newUserClass = isNewUser && !isBoosted ? 'ring-4 ring-purple-500 ring-offset-2' : '';
    const boostedUserClass = isBoosted ? 'ring-4 ring-amber-500 ring-offset-2' : '';

    const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(files[0].type)) {
                alert('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
                return;
            }

            // Validate file size (2MB = 2 * 1024 * 1024 bytes)
            const maxSize = 2 * 1024 * 1024;
            if (files[0].size > maxSize) {
                alert('File size exceeds 2MB limit. Please choose a smaller file.');
                return;
            }

            // Special handling for animated GIFs
            if (files[0].type === 'image/gif') {
                const choice = window.confirm(
                    'You selected a GIF. You have two options:\n\n' +
                    'Press OK to upload the GIF as-is with its animation (no cropping).\n' +
                    'Press Cancel to crop the first frame only (will lose animation).'
                );

                if (choice) {
                    // Option 1: Skip editor and upload GIF directly
                    const dataTransfer = new DataTransfer();
                    dataTransfer.items.add(files[0]);
                    onChange(dataTransfer.files, 'profile');
                    // Clear input
                    event.target.value = '';
                    return;
                }
                // Option 2: Continue to editor with first frame only
                // This will fall through to normal processing below
            }

            // Skip image editor in testing mode
            if (process.env.NODE_ENV === 'test') {
                const dataTransfer = new DataTransfer();
                dataTransfer.items.add(files[0]);
                onChange(dataTransfer.files, 'profile');
                // Clear input
                event.target.value = '';
                return;
            }

            setSelectedFile(files[0]);
            setIsEditorOpen(true);

            // Clear the input value to ensure onChange fires if same file is selected again
            event.target.value = '';
        }
    }, [onChange]);

    const handleSaveEditedImage = useCallback((editedFile: File) => {
        // Create a FileList-like object
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(editedFile);
        const fileList = dataTransfer.files;

        // Call the original onChange with the edited file
        onChange(fileList, 'profile');

        // Close the editor
        setIsEditorOpen(false);
        setSelectedFile(null);
    }, [onChange]);

    const handleCloseEditor = useCallback(() => {
        setIsEditorOpen(false);
        setSelectedFile(null);
    }, []);

    // console.log('ProfileImage render:', { isEditorOpen, selectedFile });

    return (
        <>
            <label
                htmlFor={canEdit ? 'profilePhoto-upload' : undefined}
                className={`w-32 h-32 block relative p-1.5 rounded-[50%] bg-aquarius ${canEdit ? 'cursor-pointer' : ''}`}
            >
                {isNewUser && !isBoosted && (
                    <span className="absolute z-10 flex items-center justify-center !right-[-8px] !top-[-8px] h-6 w-6 p-3 bg-purple-500 text-white text-[10px] font-semibold rounded-full shadow-md hover:scale-110 transition-transform duration-200">
                        New
                    </span>
                )}
                {isBoosted && (
                    <span className="absolute z-[49] flex items-center justify-center !right-[-8px] !top-[-8px] h-8 w-8 p-1.5 bg-amber-500 text-white text-[10px] font-semibold rounded-full shadow-md hover:scale-110 transition-transform duration-200">
                        <Zap size={18} />
                    </span>
                )}
                <AvatarImage testId={testId} src={src} alt={alt} wrapperClass={`${boostedUserClass} ${newUserClass}`} />
            </label>

            <label
                htmlFor="profilePhoto-upload"
                className="h-12 cursor-pointer p-1.5"
                style={{ marginLeft: '-3px' }}
            >
                {canEdit && <EditIcon />}
                <input
                    className="hidden"
                    id="profilePhoto-upload"
                    data-testid="profilePhoto-upload"
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp"
                    onChange={handleFileSelect}
                />
            </label>

            {/* Image Editor Modal - Only rendered when isEditorOpen is true */}
            {canEdit && selectedFile && (
                <ImageEditorModal
                    isOpen={isEditorOpen}
                    onClose={handleCloseEditor}
                    imageFile={selectedFile}
                    onSave={handleSaveEditedImage}
                    aspectRatio={1} // Force 1:1 ratio for profile photos
                    isProfilePhoto={true}
                />
            )}
        </>
    );
});

// Style overrides for in-place cover photo editor
const coverEditorStyles = `
  .cover-editor-controls {
    transition: opacity 0.25s ease;
  }
  .cover-editor-cropper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  .cover-editor-cropper .reactEasyCrop_Container {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
  }
  .cover-photo-actions-btn {
    background-color: rgba(0, 0, 0, 0.6);
    color: white;
    border-radius: 100%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s;
  }
  .cover-photo-actions-btn:hover {
    background-color: rgba(0, 0, 0, 0.8);
  }
`;

// Helper function to create final image (simplified version of the one in ImageEditorModal)
const createCoverPhotoFile = async (
    imageSrc: string,
    pixelCrop: { x: number; y: number; width: number; height: number },
    rotation = 0,
    fileName: string,
    fileType: string
) => {
    return new Promise<File>((resolve, reject) => {
        try {
            const image = new Image();
            image.crossOrigin = 'anonymous';

            image.onload = async () => {
                try {
                    // Create canvas with the exact dimensions needed for the cover photo display area
                    // We use a fixed height of 150px as that's the cover photo height in the UI
                    const canvas = document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    if (!ctx) {
                        throw new Error('No 2d context available');
                    }

                    // Use exact crop dimensions for consistent results
                    canvas.width = pixelCrop.width;
                    canvas.height = pixelCrop.height;

                    // Clear the canvas
                    ctx.clearRect(0, 0, canvas.width, canvas.height);

                    // Draw the image with the crop
                    ctx.drawImage(
                        image,
                        pixelCrop.x,
                        pixelCrop.y,
                        pixelCrop.width,
                        pixelCrop.height,
                        0,
                        0,
                        pixelCrop.width,
                        pixelCrop.height
                    );

                    // Check if this is a GIF (special handling)
                    const isGif = fileType === 'image/gif';

                    if (isGif) {
                        // For GIFs, create temporary representation but preserve original type
                        const mimeType = 'image/jpeg';
                        canvas.toBlob(
                            (blob) => {
                                if (!blob) {
                                    reject(new Error('Canvas to Blob conversion failed'));
                                    return;
                                }

                                // Keep original filename but mark with original type
                                const timestamp = Date.now();
                                const newFileName = `${fileName.split('.')[0]}_${timestamp}.gif`;
                                const file = new File([blob], newFileName, { type: fileType });
                                resolve(file);
                            },
                            mimeType,
                            0.95
                        );
                    } else {
                        // Convert to blob and then file - always use JPEG for non-GIF images
                        const mimeType = 'image/jpeg';
                        canvas.toBlob(
                            (blob) => {
                                if (!blob) {
                                    reject(new Error('Canvas to Blob conversion failed'));
                                    return;
                                }

                                // Create filename with appropriate extension and timestamp
                                const nameWithoutExt = fileName.split('.')[0];
                                const timestamp = Date.now();
                                const newFileName = `${nameWithoutExt}_${timestamp}.jpg`;

                                const file = new File([blob], newFileName, { type: mimeType });
                                resolve(file);
                            },
                            mimeType,
                            0.95 // Always use 95% quality here, processImageForUpload will handle further compression if needed
                        );
                    }
                } catch (error) {
                    reject(error);
                }
            };

            image.onerror = () => reject(new Error('Failed to load image'));
            image.src = imageSrc;
        } catch (error) {
            reject(error);
        }
    });
};

export const CoverImage = ({
    src,
    testId,
    onChange,
    canEdit,
    createdAt,
    alt = '',
    // Ignore the props that CoverImage doesn't use
    isBoosted: _isBoosted,
    ...rest
}: InputImageProps) => {
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [isInPlaceEditing, setIsInPlaceEditing] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [imageUrl, setImageUrl] = useState<string>(src || '');
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<{
      x: number; y: number; width: number; height: number
    } | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Track the original image dimensions to validate crop
    const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

    // Auto-clear error message after 3 seconds
    useEffect(() => {
        if (error) {
            const timer = setTimeout(() => {
                setError(null);
            }, 3000);

            // Clean up timer on unmount or when error changes
            return () => clearTimeout(timer);
        }
    }, [error]);

    // Create a ref for the cover container to measure dimensions
    const coverContainerRef = useRef<HTMLDivElement>(null);

    // Validate that crop area is within image bounds
    const isCropWithinBounds = (
        crop: { x: number; y: number; width: number; height: number },
        imageWidth: number,
        imageHeight: number
    ): boolean => {
        if (!crop) return false;

        // Check that crop is fully contained within the image
        const isValid =
        crop.x >= 0 &&
        crop.y >= 0 &&
        crop.x + crop.width <= imageWidth &&
        crop.y + crop.height <= imageHeight;

        return isValid;
    };

    // Calculate the aspect ratio based on the container dimensions
    const getContainerAspectRatio = useCallback(() => {
        if (coverContainerRef.current) {
            const width = coverContainerRef.current.clientWidth;
            const height = coverContainerRef.current.clientHeight;
            return width / height;
        }
        return 16 / 6; // Default aspect ratio if container not measured
    }, []);

    // Update imageUrl when src changes (for existing cover images)
    useEffect(() => {
        if (src && src !== imageUrl) {
            setImageUrl(src);
        }
    }, [src]);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            // Validate file type
            const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!validTypes.includes(files[0].type)) {
                alert('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
                return;
            }

            // Validate file size (2MB = 2 * 1024 * 1024 bytes)
            const maxSize = 2 * 1024 * 1024;
            if (files[0].size > maxSize) {
                alert('File size exceeds 2MB limit. Please choose a smaller file.');
                return;
            }

            // Special handling for animated GIFs
            if (files[0].type === 'image/gif') {
                const choice = window.confirm(
                    'You selected a GIF. You have two options:\n\n' +
                    'Press OK to upload the GIF as-is with its animation (no cropping).\n' +
                    'Press Cancel to crop the first frame only (will lose animation).'
                );

                if (choice) {
                    // Option 1: Skip editor and upload GIF directly
                    const dataTransfer = new DataTransfer();
                    dataTransfer.items.add(files[0]);
                    onChange(dataTransfer.files, 'cover');
                    // Clear input
                    event.target.value = '';
                    return;
                }
                // Option 2: Continue to editor with first frame only
                // This will fall through to normal processing below
            }

            setSelectedFile(files[0]);

            // Create a URL for the selected file
            const url = URL.createObjectURL(files[0]);
            setImageUrl(url);

            // Load the image to get dimensions
            const img = new Image();
            img.onload = () => {
                setImageDimensions({ width: img.width, height: img.height });
            };
            img.src = url;

            // Start in-place editing for new uploads
            setIsInPlaceEditing(true);
            setCrop({ x: 0, y: 0 });
            setZoom(1);

            // Clear the input value to ensure onChange fires if same file is selected again
            event.target.value = '';
        }
    };

    const handleSaveEditedImage = (editedFile: File) => {
        // Create a FileList-like object
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(editedFile);
        const fileList = dataTransfer.files;

        // Call the original onChange with the edited file
        onChange(fileList, 'cover');

        // Close the editor modal if it was open
        setIsEditorOpen(false);
        setSelectedFile(null);
    };

    // For the in-place editor save
    const handleSaveInPlaceEdit = async () => {
        if (!selectedFile && !src) {
            console.error('No image to save');
            return;
        }

        setIsSaving(true);
        setError(null);

        try {
            if (!croppedAreaPixels) {
                throw new Error('No crop area selected');
            }

            // Validate that crop is within image bounds
            if (!isCropWithinBounds(croppedAreaPixels, imageDimensions.width, imageDimensions.height)) {
                throw new Error('Crop area is outside image boundaries. Try zooming in!');
            }

            const sourceFile = selectedFile || { name: 'cover-photo.jpg' };
            // Get the file type if available, default to JPEG if not
            const fileType = selectedFile ? selectedFile.type : 'image/jpeg';

            const editedFile = await createCoverPhotoFile(
                imageUrl,
                croppedAreaPixels,
                0,
                sourceFile.name,
                fileType
            );

            // Process the edited file based on size and type
            const processedFile = await processImageForUpload(editedFile);

            handleSaveEditedImage(processedFile);
            setIsInPlaceEditing(false);
        } catch (e) {
            const errorMsg = e instanceof Error ? e.message : 'Failed to save cover photo';
            console.error('Error saving cover photo:', e);
            setError(errorMsg);
            alert(errorMsg);
        } finally {
            setIsSaving(false);
        }
    };

    // Handle crop complete
    const onCropComplete = useCallback((
        croppedArea: { x: number; y: number; width: number; height: number },
        croppedAreaPixels: { x: number; y: number; width: number; height: number }
    ) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    // Measure container when editing starts
    useEffect(() => {
        if (isInPlaceEditing && coverContainerRef.current) {
        // Reset crop and zoom when editing starts
            setCrop({ x: 0, y: 0 });
            setZoom(1);
        }
    }, [isInPlaceEditing, getContainerAspectRatio]);

    // Cleanup URLs when component unmounts
    useEffect(() => {
        return () => {
            if (selectedFile && imageUrl) {
                URL.revokeObjectURL(imageUrl);
            }
        };
    }, [selectedFile, imageUrl]);

    return (
        <>
            <style>{coverEditorStyles}</style>
            <div ref={coverContainerRef} className="relative w-full h-[150px] rounded-lg bg-neutral-700 mb-0">
                {isInPlaceEditing ? (
                    <div className="w-full h-full">
                        <div className="cover-editor-cropper">
                            <Cropper
                                image={imageUrl}
                                crop={crop}
                                zoom={zoom}
                                aspect={getContainerAspectRatio()}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                                showGrid={true}
                                objectFit="horizontal-cover"
                                zoomWithScroll={true}
                                restrictPosition={false}
                            />
                        </div>

                        {/* Zoom and save controls */}
                        <div className="absolute bottom-4 right-4 z-10 flex items-center gap-2 cover-editor-controls">
                            <button
                                className="cover-photo-actions-btn"
                                onClick={() => setZoom(Math.max(1, zoom - 0.1))}
                                disabled={zoom <= 1}
                            >
                                <ZoomOutIcon />
                            </button>
                            <button
                                className="cover-photo-actions-btn"
                                onClick={() => setZoom(Math.min(3, zoom + 0.1))}
                            >
                                <ZoomInIcon />
                            </button>
                            <button
                                className="cover-photo-actions-btn ml-2"
                                onClick={handleSaveInPlaceEdit}
                                disabled={isSaving}
                            >
                                <SaveIcon />
                            </button>
                            <button
                                className="cover-photo-actions-btn"
                                onClick={() => setIsInPlaceEditing(false)}
                            >
                                <span>✕</span>
                            </button>
                        </div>

                        {/* Helper text */}
                        <div className="absolute top-2 left-2 z-10 bg-black bg-opacity-50 text-white text-xs p-2 rounded">
                      Drag to position • Use zoom buttons to resize
                        </div>

                        {/* Error message if present */}
                        {error && (
                            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-20 bg-red-500 text-white text-xs p-2 rounded-md">
                                {error}
                            </div>
                        )}
                    </div>
                ) : (
                    <>
                        <img
                            data-testid={testId}
                            className="w-full h-full object-cover rounded-lg"
                            src={src ? src : noCoverImage}
                            alt={alt}
                        />
                        {canEdit && (
                            <div className="absolute top-4 right-4 flex gap-2">
                                {/* Upload new cover photo button */}
                                <label
                                    htmlFor="coverPhoto-upload"
                                    className="h-8 w-8 flex items-center justify-center bg-white bg-opacity-80 rounded-full cursor-pointer"
                                >
                                    <EditIcon />
                                    <input
                                        className="hidden"
                                        id="coverPhoto-upload"
                                        data-testid="coverPhoto-upload"
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.gif,.webp"
                                        onChange={handleFileSelect}
                                    />
                                </label>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};
